import { FC } from 'react'
import styles from './twoWaySplit.module.scss'

interface SplitSide {
  color: string
  // null = not collected yet for this range (drawn as a dash, not a 0).
  count: number | null
  label: string
}

interface TwoWaySplitProps {
  left: SplitSide
  right: SplitSide
}

const percentOf = (part: number, total: number) =>
  total > 0 ? Math.round((part / total) * 100) : 0

// A two-part bar with a label, count and share under each end — the body of
// New vs Returning and Organic vs Paid.
export const TwoWaySplit: FC<TwoWaySplitProps> = ({ left, right }) => {
  const hasData = left.count !== null && right.count !== null
  const total = (left.count ?? 0) + (right.count ?? 0)

  const renderSide = (side: SplitSide, align: 'end' | 'start') => (
    <div className={`${styles.side} ${align === 'end' ? styles.end : ''}`}>
      <span className={styles.dot} style={{ backgroundColor: side.color }} />
      <div className={styles.text}>
        <span className={styles.label}>{side.label}</span>
        <span className={styles.figures}>
          <span className={styles.count}>
            {side.count === null ? '–' : side.count.toLocaleString('en-US')}
          </span>
          {hasData && (
            <span className={styles.share}>
              {percentOf(side.count ?? 0, total)}%
            </span>
          )}
        </span>
      </div>
    </div>
  )

  return (
    <div className={styles.split}>
      <div className={styles.bar}>
        {hasData && total > 0 ? (
          [left, right].map((side) =>
            side.count ? (
              <span
                className={styles.segment}
                key={side.label}
                style={{ backgroundColor: side.color, flexGrow: side.count }}
              />
            ) : null
          )
        ) : (
          <span className={`${styles.segment} ${styles.empty}`} />
        )}
      </div>
      <div className={styles.sides}>
        {renderSide(left, 'start')}
        {renderSide(right, 'end')}
      </div>
    </div>
  )
}
